import { useMemo, useState } from "react";
import type { ScheduleView } from "@qlik-scheduler/shared";

import { ApiError } from "../../api/client.js";
import {
  useDeleteSchedule,
  useResumeSchedule,
  useRunScheduleNow,
  useSchedules,
} from "../../api/queries.js";
import { Button } from "../../components/Button.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import { Skeleton } from "../../components/Skeleton.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Table, TBody, Td, Th, THead, Tr } from "../../components/Table.js";
import { useToast } from "../../components/Toast.js";
import { ScheduleCreateModal } from "../ScheduleCreateModal.js";

type TabKey = "apps" | "schedules" | "history" | "active";
type SortKey =
  | "scheduleName"
  | "appName"
  | "connectionName"
  | "frequency"
  | "nextRun"
  | "lastRun"
  | "status";
type SortDir = "asc" | "desc";

export function SchedulesTab({ onSwitchTab }: { onSwitchTab: (tab: TabKey) => void }) {
  const toast = useToast();
  const { data, isLoading, error } = useSchedules();
  const runNow = useRunScheduleNow();
  const resume = useResumeSchedule();
  const del = useDeleteSchedule();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ScheduleView | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("nextRun");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const rows = useMemo(() => {
    const list = [...(data ?? [])];
    list.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton width={140} height={14} />
          <Skeleton width={170} height={40} rounded="full" />
        </div>
        <div className="card p-4 space-y-3">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton width="20%" height={12} />
              <Skeleton width="20%" height={12} />
              <Skeleton width="15%" height={12} />
              <Skeleton width="20%" height={12} />
              <Skeleton width="15%" height={12} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) return <ErrorAlert>{describeError(error)}</ErrorAlert>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-extrabold uppercase tracking-[0.04em] text-ink">
          Schedules{" "}
          <span className="ml-2 text-[12px] font-medium normal-case tracking-normal text-ink-soft">
            ({rows.length})
          </span>
        </h2>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          Create Schedule
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="card empty-surface">
          <p className="text-base font-semibold text-ink">No schedules yet</p>
          <p>
            Click <strong>Create Schedule</strong> to add your first one.
          </p>
        </div>
      ) : (
        <Table>
          <THead>
            <tr>
              <Th
                sortable
                sorted={sortKey === "scheduleName" ? sortDir : null}
                onSort={() => toggleSort("scheduleName")}
              >
                Name
              </Th>
              <Th
                sortable
                sorted={sortKey === "appName" ? sortDir : null}
                onSort={() => toggleSort("appName")}
              >
                App
              </Th>
              <Th
                sortable
                sorted={sortKey === "connectionName" ? sortDir : null}
                onSort={() => toggleSort("connectionName")}
              >
                Connection
              </Th>
              <Th
                sortable
                sorted={sortKey === "frequency" ? sortDir : null}
                onSort={() => toggleSort("frequency")}
              >
                Frequency
              </Th>
              <Th
                sortable
                sorted={sortKey === "nextRun" ? sortDir : null}
                onSort={() => toggleSort("nextRun")}
              >
                Next Run
              </Th>
              <Th
                sortable
                sorted={sortKey === "lastRun" ? sortDir : null}
                onSort={() => toggleSort("lastRun")}
              >
                Last Run
              </Th>
              <Th
                sortable
                sorted={sortKey === "status" ? sortDir : null}
                onSort={() => toggleSort("status")}
              >
                Status
              </Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </THead>
          <TBody>
            {rows.map((s) => (
              <Tr key={s.id} className={s.isSuspended ? "bg-yellow-50" : ""}>
                <Td className="font-semibold text-ink">{s.scheduleName}</Td>
                <Td>{s.appName}</Td>
                <Td>{s.connectionName}</Td>
                <Td>{s.frequency ?? (s.scheduleType === "ONCE" ? "Once" : "—")}</Td>
                <Td>{formatDateTime(s.nextRun)}</Td>
                <Td>{formatDateTime(s.lastRun)}</Td>
                <Td>
                  <StatusCell s={s} />
                </Td>
                <Td className="text-right">
                  <div className="inline-flex flex-wrap justify-end gap-1.5">
                    {s.isSuspended ? (
                      <Button
                        variant="ghost-sm"
                        onClick={async () => {
                          try {
                            await resume.mutateAsync(s.id);
                            toast.push("success", `Resumed "${s.scheduleName}"`);
                          } catch (err) {
                            toast.push("error", describeError(err));
                          }
                        }}
                      >
                        Resume
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost-sm"
                      onClick={async () => {
                        try {
                          await runNow.mutateAsync(s.id);
                          toast.push("success", `Queued "${s.scheduleName}" — see Active Reloads.`);
                          onSwitchTab("active");
                        } catch (err) {
                          toast.push("error", describeError(err));
                        }
                      }}
                    >
                      Run Now
                    </Button>
                    <Button variant="ghost-sm" disabled title="Coming in Phase 2">
                      Edit
                    </Button>
                    <Button variant="ghost-sm" disabled title="Coming in Phase 2">
                      View Metrics
                    </Button>
                    <Button variant="danger-sm" onClick={() => setDeleteTarget(s)}>
                      Delete
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      <ScheduleCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          toast.push("success", "Schedule created");
        }}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete schedule"
        message={
          deleteTarget ? (
            <p>
              Delete <strong>{deleteTarget.scheduleName}</strong>? This cannot be undone.
            </p>
          ) : null
        }
        confirmLabel="Delete"
        destructive
        busy={del.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await del.mutateAsync(deleteTarget.id);
            toast.push("success", "Schedule deleted");
            setDeleteTarget(null);
          } catch (err) {
            toast.push("error", describeError(err));
          }
        }}
      />
    </div>
  );
}

function StatusCell({ s }: { s: ScheduleView }) {
  if (s.isSuspended) {
    return (
      <StatusBadge
        tone="yellow"
        title={`Suspended: ${s.suspendedReason ?? "unknown reason"}`}
      >
        Suspended
      </StatusBadge>
    );
  }
  if (!s.isActive) {
    return <StatusBadge tone="grey">Inactive</StatusBadge>;
  }
  return <StatusBadge tone="green">Active</StatusBadge>;
}

function sortValue(s: ScheduleView, key: SortKey): string | number | null {
  switch (key) {
    case "scheduleName":
      return s.scheduleName.toLowerCase();
    case "appName":
      return s.appName.toLowerCase();
    case "connectionName":
      return s.connectionName.toLowerCase();
    case "frequency":
      return s.frequency ?? "";
    case "nextRun":
      return s.nextRun ? new Date(s.nextRun).getTime() : null;
    case "lastRun":
      return s.lastRun ? new Date(s.lastRun).getTime() : null;
    case "status":
      if (s.isSuspended) return 2;
      if (!s.isActive) return 1;
      return 0;
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
